"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import { CloseIcon } from "@/shared/ui/atoms";
import { CustomButton } from "@/shared/ui/molecules";
import { submitDemandSignal, type DemandSignalType } from "@/features/demand-signal/api";
import { getSessionKey } from "@/features/demand-signal/lib/sessionKey";
import { pushDemandSignalEvent } from "@/features/demand-signal/lib/gtm";
import { isValidDemandContact, normalizeDemandContact } from "@/features/demand-signal/lib/contact";
type DemandSignalModalProps = {
  open: boolean;
  hospitalOriginId: number;
  signalType: DemandSignalType;
  onClose: () => void;
};
const CONTENT_MAX_LENGTH = 2000;

/** 네이티브 dialog로 포커스 가두기·배경 비활성화·닫은 뒤 포커스 복원을 유지한다. */
const DemandSignalModal = ({
  open,
  hospitalOriginId,
  signalType,
  onClose
}: DemandSignalModalProps) => {
  const t = useTranslations("demandSignal");
  const isConsult = signalType === "CONSULT_WAITLIST";
  const [content, setContent] = useState("");
  const [contactValue, setContactValue] = useState("");
  const [contactTouched, setContactTouched] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);
  const submittingRef = useRef(false);
  const id = useId();
  const title = isConsult ? t("consultTitle") : t("reviewTitle");
  const validContact = isValidDemandContact(contactValue);
  const showContactError = contactTouched && !validContact;
  const canSubmit = !submitting && (isConsult ? validContact && consent : content.trim().length > 0);
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    dialog?.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = overflow;
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);
  useEffect(() => {
    if (done) successRef.current?.focus();
  }, [done]);
  const close = () => {
    if (submittingRef.current) return;
    setContent("");
    setContactValue("");
    setContactTouched(false);
    setConsent(false);
    setDone(false);
    setFailed(false);
    onClose();
  };
  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFailed(false);
    try {
      await submitDemandSignal(hospitalOriginId, {
        signalType,
        content: content.trim() || undefined,
        contactValue: isConsult ? normalizeDemandContact(contactValue) : undefined,
        consent: isConsult ? consent : undefined,
        sessionKey: isConsult ? undefined : getSessionKey()
      });
      pushDemandSignalEvent("demand_signal_submit", hospitalOriginId, signalType);
      setDone(true);
    } catch {
      setFailed(true);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  if (!open || typeof document === "undefined") return null;
  return createPortal(<dialog ref={dialogRef} aria-labelledby={`${id}-title`} aria-describedby={done ? `${id}-success` : `${id}-description`} aria-modal="true" onCancel={event => {
    event.preventDefault();
    close();
  }} onClick={event_0 => {
    if (event_0.target === event_0.currentTarget) close();
  }} className="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none items-end justify-center overflow-hidden border-0 bg-transparent p-0 text-gray-900 backdrop:bg-gray-900/50 open:flex md:items-center md:p-6">
      <section className="flex max-h-[calc(100dvh-16px)] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl md:max-h-[calc(100dvh-48px)] md:max-w-[560px] md:rounded-3xl">
        <header className="flex shrink-0 items-start justify-between gap-4 px-6 pb-5 pt-6 md:px-8 md:pt-8">
          <div>
            <p className="mb-2 text-xs font-semibold tracking-wide text-primary-600">{t("eyebrow")}</p>
            <h2 id={`${id}-title`} className="text-2xl font-bold leading-8 tracking-tight">{title}</h2>
          </div>
          <button type="button" aria-label={t("close")} disabled={submitting} onClick={close} className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-40">
            <CloseIcon size={20} />
          </button>
        </header>

        {done ? <div className="overflow-y-auto px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:px-8 md:pb-8">
            <div className="py-7 text-center" role="status">
              <div aria-hidden="true" className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-3xl text-primary-600">✓</div>
              <h3 ref={successRef} tabIndex={-1} className="text-xl font-semibold leading-7 outline-none">{isConsult ? t("consultSuccess") : t("reviewSuccess")}</h3>
              <p id={`${id}-success`} className="mx-auto mt-3 max-w-[340px] text-sm leading-6 text-gray-600">
                {isConsult ? t("consultSuccessDetail") : t("reviewSuccessDetail")}
              </p>
            </div>
            <CustomButton onClick={close} className="mt-4 w-full rounded-xl bg-primary-600 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500">{t("confirm")}</CustomButton>
          </div> : <form className="flex min-h-0 flex-col" onSubmit={event_1 => {
        event_1.preventDefault();
        void handleSubmit();
      }} noValidate>
            <div className="min-h-0 space-y-6 overflow-y-auto px-6 pb-6 md:px-8">
              <div id={`${id}-description`} className="rounded-2xl bg-primary-50 px-4 py-3.5 text-sm leading-6 text-gray-700">
                <p className="mb-1 font-semibold text-primary-800">{t("noticeTitle")}</p>
                <p>{isConsult ? t("consultNotice") : t("reviewNotice")}</p>
              </div>

              <div>
                <label htmlFor={`${id}-content`} className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  {isConsult ? t("consultLabel") : t("reviewLabel")}<span className="text-xs font-normal text-gray-500">{isConsult ? t("optional") : t("required")}</span>
                </label>
                <textarea id={`${id}-content`} value={content} maxLength={CONTENT_MAX_LENGTH} disabled={submitting} required={!isConsult} onChange={event_2 => setContent(event_2.target.value)} placeholder={isConsult ? t("consultPlaceholder") : t("reviewPlaceholder")} aria-describedby={`${id}-content-help`} className="block min-h-32 w-full resize-y rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm leading-6 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 disabled:bg-gray-100 md:min-h-36" />
                <div id={`${id}-content-help`} className="mt-2 flex justify-between gap-3 text-xs leading-5 text-gray-500">
                  <span>{t("contentHelp")}</span><span className="shrink-0 tabular-nums">{content.length.toLocaleString()} / {CONTENT_MAX_LENGTH.toLocaleString()}</span>
                </div>
              </div>

              {isConsult && <>
                  <div>
                    <label htmlFor={`${id}-contact`} className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">{t("contactLabel")}<span className="text-xs font-normal text-gray-500">{t("required")}</span></label>
                    <input id={`${id}-contact`} type="tel" inputMode="tel" autoComplete="tel" value={contactValue} maxLength={20} disabled={submitting} required onChange={event_3 => setContactValue(event_3.target.value)} onBlur={() => setContactTouched(true)} placeholder="010-1234-5678" aria-invalid={showContactError} aria-describedby={`${id}-contact-help`} className={`w-full rounded-xl border bg-white px-4 py-3 text-sm leading-6 outline-none transition-colors placeholder:text-gray-400 focus:ring-2 disabled:bg-gray-100 ${showContactError ? "border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-primary-500 focus:ring-primary-100"}`} />
                    <p id={`${id}-contact-help`} className={`mt-2 text-xs leading-5 ${showContactError ? "text-red-600" : "text-gray-500"}`}>{showContactError ? t("contactError") : t("contactHelp")}</p>
                  </div>
                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-4">
                    <input type="checkbox" checked={consent} disabled={submitting} onChange={event_4 => setConsent(event_4.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-primary-600" />
                    <span className="text-sm leading-5 text-gray-700"><span className="font-semibold">{t("consentTitle")} <span className="text-primary-600">({t("required")})</span></span><span className="mt-1.5 block text-xs leading-5 text-gray-500">{t("consentDetail")}</span></span>
                  </label>
                </>}
              {failed && <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm leading-5 text-red-600">{t("failed")}</p>}
            </div>
            <footer className="shrink-0 border-t border-gray-100 px-6 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 md:px-8 md:pb-6">
              <CustomButton type="submit" onClick={() => {}} disabled={!canSubmit} ariaBusy={submitting} className="w-full rounded-xl bg-primary-600 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400">
                {submitting ? t("sending") : t("send")}
              </CustomButton>
            </footer>
          </form>}
      </section>
    </dialog>, document.body);
};
export default DemandSignalModal;